import React, { useState, useEffect } from 'react';

async function searchOpenLibrary(query) {
    if (!query.trim()) return [];
    try {
        const url = `https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=8&fields=title,author_name,cover_i`;
        const res = await fetch(url);
        if (!res.ok) return [];
        const data = await res.json();
        return (data.docs || []).map(d => ({
            title: d.title,
            author: (d.author_name || [])[0] || '',
            coverId: d.cover_i || null,
        }));
    } catch {
        return [];
    }
}

export default function EditSeriesModal({ series, onClose, onSave }) {
    const [name, setName] = useState(series.title);
    const [author, setAuthor] = useState(series.author || '');
    const [books, setBooks] = useState((series.seriesBooks || []).map(b => b.title));
    const [editingIdx, setEditingIdx] = useState(null);
    const [editDraft, setEditDraft] = useState('');
    const [addMode, setAddMode] = useState('manual');
    const [addDraft, setAddDraft] = useState('');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    function startEdit(i) {
        setEditingIdx(i);
        setEditDraft(books[i]);
    }

    function commitEdit(i) {
        const t = editDraft.trim();
        if (t) setBooks(b => b.map((v, j) => j === i ? t : v));
        setEditingIdx(null);
    }

    function addManual() {
        const t = addDraft.trim();
        if (!t) return;
        setBooks(b => [...b, t]);
        setAddDraft('');
    }

    async function runSearch() {
        if (!query.trim()) return;
        setSearching(true);
        setResults([]);
        setResults(await searchOpenLibrary(query));
        setSearching(false);
    }

    function addFromSearch(r) {
        setBooks(b => [...b, r.title]);
        setQuery('');
        setResults([]);
    }

    function submit(e) {
        e.preventDefault();
        if (!name.trim() || books.length === 0) return;
        onSave({
            title: name.trim(),
            author: author.trim(),
            seriesBooks: books.map(t => ({ title: t })),
        });
    }

    const canSave = !!name.trim() && books.length > 0;

    return (
        <div className="modal-scrim" onClick={onClose}>
            <form className="modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
                <header className="modal-hd">
                    <h2>Edit series</h2>
                    <button type="button" className="modal-close" onClick={onClose}>close ×</button>
                </header>

                <div className="modal-body">
                    <div className="gate-field">
                        <label className="gate-label">Series name</label>
                        <input
                            className="gate-input"
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="gate-field">
                        <label className="gate-label">Author</label>
                        <input
                            className="gate-input"
                            type="text"
                            value={author}
                            onChange={e => setAuthor(e.target.value)}
                        />
                    </div>

                    <div className="gate-field">
                        <label className="gate-label">Books in this series</label>
                        <ul className="series-list">
                            {books.map((t, i) => (
                                <li className="series-list-item" key={i}>
                                    <span className="series-list-num">{String(i + 1).padStart(2, '0')}</span>
                                    {editingIdx === i ? (
                                        <input
                                            className="gate-input series-list-edit-input"
                                            value={editDraft}
                                            onChange={e => setEditDraft(e.target.value)}
                                            onBlur={() => commitEdit(i)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') { e.preventDefault(); commitEdit(i); }
                                                if (e.key === 'Escape') setEditingIdx(null);
                                            }}
                                            autoFocus
                                        />
                                    ) : (
                                        <span
                                            className="series-list-title"
                                            onClick={() => startEdit(i)}
                                            title="Click to rename"
                                        >
                                            {t}
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        className="series-list-x"
                                        onClick={() => setBooks(b => b.filter((_, j) => j !== i))}
                                    >
                                        ×
                                    </button>
                                </li>
                            ))}
                            {books.length === 0 && (
                                <li style={{ fontStyle: 'italic', color: 'var(--ink-3)', padding: '6px 4px' }}>
                                    No books — add at least one.
                                </li>
                            )}
                        </ul>

                        <div className="toggle-row" style={{ marginTop: 10, marginBottom: 8 }}>
                            <button type="button" className={'toggle-btn' + (addMode === 'manual' ? ' is-on' : '')} onClick={() => setAddMode('manual')}>Manual</button>
                            <button type="button" className={'toggle-btn' + (addMode === 'search' ? ' is-on' : '')} onClick={() => { setAddMode('search'); setResults([]); }}>Search</button>
                        </div>

                        {addMode === 'manual' && (
                            <div className="series-input-row">
                                <input
                                    className="gate-input"
                                    type="text"
                                    placeholder="Book title — Enter to add"
                                    value={addDraft}
                                    onChange={e => setAddDraft(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addManual(); } }}
                                />
                                <button type="button" className="add-btn" onClick={addManual}>
                                    <span className="add-btn-plus">+</span><span>add</span>
                                </button>
                            </div>
                        )}

                        {addMode === 'search' && (
                            <div>
                                <div className="search-row">
                                    <input
                                        className="gate-input"
                                        type="text"
                                        placeholder="Search Open Library…"
                                        value={query}
                                        onChange={e => setQuery(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                                    />
                                    <button type="button" className="search-btn" onClick={runSearch} disabled={!query.trim()}>
                                        Search →
                                    </button>
                                </div>
                                {searching && <div className="search-status">Searching…</div>}
                                {results.length > 0 && (
                                    <ul className="search-results-list">
                                        {results.map((r, i) => (
                                            <li key={i} className="search-result-item" onClick={() => addFromSearch(r)}>
                                                {r.coverId && (
                                                    <img className="search-result-cover" src={`https://covers.openlibrary.org/b/id/${r.coverId}-S.jpg`} alt="" />
                                                )}
                                                <div className="search-result-info">
                                                    <div className="search-result-title">{r.title}</div>
                                                    {r.author && <div className="search-result-author">{r.author}</div>}
                                                </div>
                                                <span className="search-result-pick">+ add</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <footer className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn-primary" disabled={!canSave}>Save changes →</button>
                </footer>
            </form>
        </div>
    );
}
